import { claimWithdrawal as claimWithdrawalV1, } from "@bracket/contractor-next/bracket-vault";
import { claimWithdrawal as claimWithdrawalV2, } from "@bracket/contractor-next/bracket-vault-v2";
import type { VaultTransaction, } from "@bracket/prisma";
import type { WEIString, } from "@bracket/types";
import type { LoadedVault, ResolverResult, VaultAddress, } from "@types";
import { isUserWalletRejection, trackVaultEvent, } from "@utils";
import { wagmiConfig as config, } from "@wagmiconfig";
import { DateTime, } from "luxon";



export declare type ClaimProps = {
	vault: LoadedVault;
	shares: WEIString|bigint;
	transaction: VaultTransaction;
};



/**
 * Claims a withdrawal from a vault
 * 
 * @param {ClaimProps} props - The properties required to claim a withdrawal
 * @param {LoadedVault} props.vault - The vault to claim from
 * @param {AmountString} props.shares - The number of shares to claim
 * @param {VaultTransaction} props.transaction - The transaction details containing epoch, scheduled time and salt
 * @returns {Promise<ResolverResult>} A promise that resolves to the result of the claim operation
 * @throws {Error} If the claim operation fails and is not a user wallet rejection
 */
export const claim = async ({
	vault,
	shares,
	transaction: { epoch_available, scheduled_time, salt, },
}: ClaimProps): Promise<ResolverResult> => {

	try {

		const { 
			symbol,
			denomination,
			denomination: { decimals, },
			nav: {
				nav,
			},
		} = vault;

		const claimWithdrawal = vault.version === "2" ? claimWithdrawalV2 : claimWithdrawalV1;
		const hash = await claimWithdrawal({
			vault: vault.address as VaultAddress,
			epoch: epoch_available,
			shares: shares.toString(),
			timestamp: DateTime.fromISO(scheduled_time as unknown as string).toSeconds(),
			salt_bytes: salt,
			config,
		});

		await trackVaultEvent({
			type: "withdraw_claim",
			value: Number(shares)*(Number(nav)/(10**decimals)),
			symbol: symbol || "unknown",
			valueType: "asset",
			denomination,
		});

		return {
			success: true,
			hash,
		};

	} catch (error) {

		if(isUserWalletRejection(error)) {

			return {
				success: false,
				rejected: true,
			};

		}

		return {
			success: false,
			rejected: false,
			error: error as Error,
		};

	}

};
